import { gptStore } from "@/store/gptStore";
import { isGptSlot } from "@/ts/guards";
import type { GPTModule, GPTModuleConfig } from "@/ts/module";
import type { DisplaySlotConfig, Exactly, GPTStore, SlotTargeting, } from "@/ts/types";
import { appendHtmlToHead } from "@/utils/dom";
import { getGoogleTag } from "@/utils/googletag";
import { getSizeForSlot } from "@/utils/slots";
import { GPT_TAG } from "@/vendors/gptTag";
export class GPT implements Exactly<GPTModule, GPT> {
    private config: GPTModuleConfig;
    name = "GPT" as const;
    constructor(config: GPTModuleConfig) {
        this.config = config;
    }
    async init(): Promise<void> {
        const { setState, getState } = gptStore;
        const { actions, selectors } = getState();
        const gt = getGoogleTag();
        window.googletag = window.googletag || gt;
        if (this.config?.injectScriptTag) {
            appendHtmlToHead(GPT_TAG);
        }
        const gptConfig = this.config;
        if (gptConfig) {
            //mantis carousel case only
            setState((state: GPTStore) => ({
                ...state,
                slotsTemplate: gptConfig.slotsTemplate,
                usePageAdUnitPathForCarousel: gptConfig.usePageAdUnitPathForCarousel,
                slotSizes: gptConfig.adSizesGPT,
            }));
            gptStore.subscribe(() => selectors.getUpdatedSlotConfigs()[0] || {}, ({ slotConfigs, commandId }) => {
                if (!(slotConfigs || commandId)) {
                    return;
                }
                defineSlots(slotConfigs, gptConfig).then((slots) => {
                    actions.setSlotsByCommandId(commandId, slots);
                });
            });
        }
    }
    async run(commandId: number, additionalTargeting: Record<string, SlotTargeting> | undefined = undefined) {
        const { selectors } = gptStore.getState();
        const { injectScriptTag = false } = this.config || {};
        const slotConfigs = selectors.getSlotConfigsByCommandId(commandId);
        const slots = selectors.getSlotsByCommandId(commandId);
        const combinedTargeting = extractTargeting(slotConfigs, additionalTargeting);
        if (Object.keys(slots).length > 0) {
            await setTargetingForSlots(slots, combinedTargeting);
            display(slotConfigs, slots, injectScriptTag);
        }
    }
}
const extractTargeting = (slotConfigs: DisplaySlotConfig[], additionalTargeting: Record<string, SlotTargeting> | undefined): Record<string, SlotTargeting> => {
    return slotConfigs.reduce<Record<string, SlotTargeting>>((combinedTargeting, { slotId, targeting }) => {
        combinedTargeting[slotId] = {
            ...targeting,
            ...additionalTargeting?.[slotId],
        };
        return combinedTargeting;
    }, {});
};
const defineSlots = (slotConfigs: DisplaySlotConfig[], config: GPTModuleConfig): Promise<Record<string, googletag.Slot>> => {
    return new Promise((resolve) => {
        const gt = getGoogleTag();
        gt.cmd.push(() => {
            const gptSlots: Record<string, googletag.Slot> = {};
            for (const slotCfg of slotConfigs) {
                const { slotId, adUnitPath } = slotCfg;
                if (adUnitPath) {
                    const size = getSizeForSlot(slotId, config.adSizesGPT) as googletag.GeneralSize;
                    const gptSlot = gt
                        ?.defineSlot(adUnitPath, size, slotId)
                        ?.addService(googletag.pubads());
                    if (isGptSlot(gptSlot)) {
                        gptSlots[slotId] = gptSlot;
                    }
                }
            }
            resolve(gptSlots);
        });
    });
};
const setTargetingForSlots = async (slots: Record<string, googletag.Slot>, combinedTargeting: Record<string, SlotTargeting> | undefined) => {
    const gt = getGoogleTag();
    for (const [slotId, slot] of Object.entries(slots)) {
        const slotTargeting = combinedTargeting?.[slotId];
        if (slotTargeting) {
            gt.cmd.push(() => {
                slot.updateTargetingFromMap(structuredClone(slotTargeting));
            });
        }
    }
};
const display = async (slotConfigs: DisplaySlotConfig[], slots: Record<string, googletag.Slot>, injectScriptTag: boolean) => {
    const gt = getGoogleTag();
    gt.cmd.push(() => {
        const pubads = gt.pubads() as googletag.PubAdsService & {
            isSRA: () => boolean; // isSRA wasn't included in @types/google-publisher-tag
        };
        if (!pubads.isInitialLoadDisabled()) {
            pubads.disableInitialLoad();
        }
        if (!pubads?.isSRA()) {
            pubads.enableSingleRequest();
        }
        if (injectScriptTag === true && !window.googletag.pubadsReady) {
            gt.enableServices();
        }
        waitForPubadsReady(() => {
            for (const { container } of slotConfigs) {
                gt.display(container as Element);
            }
            if (pubads.isInitialLoadDisabled()) {
                gt.pubads().refresh(Object.values(slots));
            }
        });
    });
};
const waitForPubadsReady = (callback: () => void, interval = 500) => {
    function check(): void {
        if (window.googletag.pubadsReady) {
            callback();
        }
        else {
            setTimeout(check, interval);
        }
    }
    check();
};
